import React, { FunctionComponent } from 'react'
import { View } from 'react-native'
import { ClipPath, Defs, G, Path, Rect, Svg } from 'react-native-svg'

import styled, { useTheme } from 'styled-components/native'

import { NumberBadge } from './NumberBadge'
import { tID } from '../../utils/utils'

type SideNavMessageIconProps = {
  fillColor?: string
  isFilled?: boolean
  size?: number
  unreadMessageCount: number
}

const BadgeContainer = styled.View({
  position: 'absolute',
  top: '-3px',
  right: '-4px',
})

export const SideNavMessageIcon: FunctionComponent<SideNavMessageIconProps> = ({
  fillColor,
  isFilled = false,
  size = 24,
  unreadMessageCount,
}) => {
  const { colors } = useTheme()
  const { numberBadge } = colors.icons.messageIcon
  const messageCountDisplay = unreadMessageCount > 99 ? '99+' : String(unreadMessageCount)
  const color = fillColor ?? colors.iconDefault

  return (
    <View testID={tID('SideNavMessageIcon')}>
      {unreadMessageCount > 0 ? (
        <BadgeContainer>
          <NumberBadge
            number={messageCountDisplay}
            backgroundColor={numberBadge.background}
            borderColor={numberBadge.border}
            borderWidth={1}
            style={{ padding: '2px', borderRadius: '4px' }}
            typeStyle={{ fontWeight: '700' }}
          />
        </BadgeContainer>
      ) : null}
      {/* The filled path needs to have a slightly differnt size so they both being drawn at about 28.8px */}
      <Svg width={size} height={size} viewBox={isFilled ? '2.7 2.4 27.4 27.4' : '0 0 24 24'} fill='none'>
        {isFilled ? (
          <Path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M4.09058 10.6494C4.09058 8.96919 4.09058 8.12911 4.41756 7.48738C4.70518 6.92289 5.16412 6.46395 5.7286 6.17633C6.37034 5.84935 7.21042 5.84935 8.89058 5.84935H23.9961C25.6763 5.84935 26.5164 5.84935 27.1581 6.17633C27.7226 6.46395 28.1815 6.92289 28.4692 7.48738C28.7961 8.12911 28.7961 8.96919 28.7961 10.6494V17.725C28.7961 19.4052 28.7961 20.2453 28.4692 20.887C28.1815 21.4515 27.7226 21.9104 27.1581 22.1981C26.5164 22.525 25.8309 22.525 25.5852 22.525H24.1844C23.9662 22.525 23.3317 23.231 22.5968 24.0487L22.5968 24.0487L22.5967 24.0487C22.141 24.5558 21.6466 25.1059 21.189 25.5572C20.2496 26.4966 19.0387 26.1852 18.4106 25.5572C17.7937 24.9642 17.1161 24.2008 16.5724 23.5883L16.5724 23.5883L16.5724 23.5883C16.035 22.9829 15.6286 22.525 15.5411 22.525H8.89058C7.21042 22.525 6.37034 22.525 5.7286 22.1981C5.16412 21.9104 4.70518 21.4515 4.41756 20.887C4.09058 20.2453 4.09058 19.4052 4.09058 17.725V10.6494ZM9.43286 12.2096C9.43286 11.6573 9.88058 11.2096 10.4329 11.2096H22.4666C23.0188 11.2096 23.4666 11.6573 23.4666 12.2096C23.4666 12.7619 23.0188 13.2096 22.4666 13.2096H10.4329C9.88058 13.2096 9.43286 12.7619 9.43286 12.2096ZM10.4329 15.1667C9.88058 15.1667 9.43286 15.6144 9.43286 16.1667C9.43286 16.719 9.88058 17.1667 10.4329 17.1667H18.5095C19.0618 17.1667 19.5095 16.719 19.5095 16.1667C19.5095 15.6144 19.0618 15.1667 18.5095 15.1667H10.4329Z'
            fill={color}
          />
        ) : (
          <>
            <G clipPath='url(#clip0_5596_64614)' fill={color}>
              <Path d='M18.767 17.704l-.069.017c-.397.181-1.57 1.667-2.55 2.633-.82.821-1.88.55-2.428 0-1.006-.966-2.196-2.452-2.467-2.633l-.042-.017v-1.5a1.546 1.546 0 01.876.271c.102.069.184.139.231.181.101.09.204.196.29.287.179.189.405.444.635.703.483.544 1.03 1.16 1.516 1.626l.01.011.011.01a.29.29 0 00.168.079c.018.001.03 0 .038-.004a.305.305 0 00.102-.075l.007-.007c.484-.477 1.01-1.08 1.53-1.652.242-.267.496-.541.72-.753.11-.104.25-.228.403-.335.077-.054.189-.125.326-.188.122-.055.37-.154.693-.154v1.5zm1.224-1.5v1.5h-1.224v-1.5h1.224zm1.307-2.696V7.323c0-.759 0-1.238-.03-1.6-.028-.343-.075-.449-.092-.483a1.125 1.125 0 00-.49-.491c-.034-.017-.14-.064-.484-.092-.362-.03-.84-.03-1.6-.03H5.399c-.759 0-1.238 0-1.6.03-.343.028-.448.075-.483.092-.21.108-.383.28-.49.491-.018.034-.065.14-.093.483-.03.362-.03.841-.03 1.6v6.185c0 .759 0 1.238.03 1.6.029.344.075.449.092.483.108.21.28.382.491.49.034.017.14.064.483.092.362.03.841.03 1.6.03v1.5l-.972-.004c-.782-.012-1.283-.059-1.685-.231l-.107-.05a2.622 2.622 0 01-1.045-.966l-.1-.18c-.286-.562-.287-1.296-.287-2.764V7.323c0-1.377 0-2.109.236-2.657l.05-.107c.22-.432.554-.793.965-1.045l.18-.1c.422-.215.94-.269 1.793-.282l.972-.005h13.205l.972.005c.852.013 1.37.067 1.791.281.494.251.894.652 1.146 1.146.286.56.286 1.296.286 2.764v6.185c0 1.468 0 2.202-.286 2.763a2.622 2.622 0 01-1.146 1.147l-.21.094c-.353.136-.7.176-.938.187l-.227.005v-1.5c.095 0 .209-.001.343-.02.138-.02.259-.056.352-.103.21-.108.382-.28.49-.49.017-.034.064-.139.092-.483.03-.362.03-.841.03-1.6zm-10.087 2.696v1.5H5.398v-1.5h5.813z' />
              <Path d='M17.248 7.937a.75.75 0 010 1.5H6.729a.75.75 0 110-1.5h10.519zM13.789 11.396a.75.75 0 110 1.5h-7.06a.75.75 0 010-1.5h7.06z' />
            </G>
            <Defs>
              <ClipPath id='clip0_5596_64614'>
                <Rect width={size} height={size} rx={4} fill={color} />
              </ClipPath>
            </Defs>
          </>
        )}
      </Svg>
    </View>
  )
}
